/**
 * Beautyrest — PDP Quantity Lock
 * Runs on: product pages ONLY
 *
 * Responsibilities:
 *   Disable the + button on the product form quantity input:
 *     Non-Twin XL variant selected → max qty 1 (+ disabled immediately)
 *     Twin XL variant selected     → max qty 2 (+ disabled at qty 2)
 *
 * Confirmed DOM structure (from DevTools):
 *   Form    : form.shopify-product-form inside product-form-component
 *   Qty     : quantity-selector-component inside the form
 *   + btn   : button[name="plus"] inside quantity-selector-component
 *   qty inp : input[name="quantity"] inside quantity-selector-component
 *   Variant : ?variant=XXXX in URL, or input[name="id"] in form
 *   Size btn: button elements inside fieldset — selected one has dark background
 */

// ─── Only run on product pages ────────────────────────────────────────────────
if (!window.location.pathname.startsWith('/products')) {
  // Not on a product page — do nothing
} else {

// ─── Helpers ──────────────────────────────────────────────────────────────────

function lockBtn(btn, msg) {
  if (!btn) return;
  btn.disabled = true;
  btn.setAttribute('aria-disabled', 'true');
  btn.title = msg;
  btn.style.setProperty('opacity',        '0.3',         'important');
  btn.style.setProperty('cursor',         'not-allowed', 'important');
  btn.style.setProperty('pointer-events', 'none',        'important');
}

function unlockBtn(btn) {
  if (!btn) return;
  btn.disabled = false;
  btn.removeAttribute('aria-disabled');
  btn.title = '';
  btn.style.removeProperty('opacity');
  btn.style.removeProperty('cursor');
  btn.style.removeProperty('pointer-events');
}

// ─── Selected variant detection ───────────────────────────────────────────────
// Two strategies:
//   1. Check URL ?variant= against product JSON (most reliable)
//   2. Check which size button has dark background (visual fallback)

let productVariants = []; // populated from product JSON

async function loadProductVariants() {
  try {
    // Shopify serves product JSON at /products/[handle].js
    const handle = window.location.pathname.split('/products/')[1]?.split('?')[0];
    if (!handle) return;
    const res  = await fetch('/products/' + handle + '.js', { cache: 'no-store' });
    const prod = await res.json();
    productVariants = prod.variants || [];
  } catch (e) {
    console.warn('[Beautyrest PDP]', e);
  }
}

function getSelectedVariantId() {
  // From URL
  const fromURL = new URLSearchParams(window.location.search).get('variant');
  if (fromURL) return fromURL;
  // From hidden input in form
  const hiddenId = document.querySelector(
    'product-form-component input[name="id"], form.shopify-product-form input[name="id"]'
  );
  return hiddenId ? hiddenId.value : null;
}

function isSelectedVariantTwinXL() {
  const variantId = getSelectedVariantId();

  // Strategy 1: match against product variants JSON
  if (variantId && productVariants.length) {
    const variant = productVariants.find(v => String(v.id) === String(variantId));
    if (variant) {
      const title = (variant.title || '').toLowerCase();
      const opts  = [variant.option1, variant.option2, variant.option3]
        .filter(Boolean).map(o => o.toLowerCase());
      return title.includes('twin xl') || opts.some(o => o.includes('twin xl'));
    }
  }

  // Strategy 2: check which size button is visually selected (dark background)
  // Horizon uses a dark fill on the selected pill button
  const fieldsetBtns = document.querySelectorAll(
    'product-form-component fieldset button, ' +
    'form.shopify-product-form fieldset button'
  );
  for (const btn of fieldsetBtns) {
    const style = window.getComputedStyle(btn);
    const bg    = style.backgroundColor;
    // Dark background = selected. White/transparent = unselected.
    const isDark = bg !== 'rgba(0, 0, 0, 0)' &&
                   bg !== 'rgb(255, 255, 255)' &&
                   bg !== 'transparent' &&
                   bg !== '';
    if (isDark && btn.textContent.trim().toLowerCase().includes('twin xl')) {
      return true;
    }
  }

  return false;
}

// ─── Lock/unlock the PDP + button ─────────────────────────────────────────────

function applyPDPQtyLock() {
  const comp = document.querySelector(
    'product-form-component quantity-selector-component, ' +
    'form.shopify-product-form quantity-selector-component'
  );
  if (!comp) return;

  const plusBtn  = comp.querySelector('button[name="plus"]');
  const qtyInput = comp.querySelector('input[name="quantity"]');
  if (!plusBtn || !qtyInput) return;

  const isTwinXL = isSelectedVariantTwinXL();
  const qty      = parseInt(qtyInput.value, 10) || 1;
  const limit    = isTwinXL ? 2 : 1;

  if (qty >= limit) {
    lockBtn(plusBtn, isTwinXL
      ? 'Twin XL items are purchased as a pair of 2 — maximum reached.'
      : 'Only 1 per size can be added at a time.');
  } else {
    unlockBtn(plusBtn);
  }
}

// ─── Capture-phase click block ────────────────────────────────────────────────

document.addEventListener('click', function (e) {
  const btn  = e.target.closest('button[name="plus"]');
  const comp = btn?.closest('quantity-selector-component');
  if (!btn || !comp) return;

  // Only act on PDP form context
  const inPDPForm = comp.closest('product-form-component, form.shopify-product-form');
  if (!inPDPForm) return;

  const isTwinXL = isSelectedVariantTwinXL();
  const qtyInput = comp.querySelector('input[name="quantity"]');
  const qty      = parseInt(qtyInput?.value, 10) || 1;

  if (qty >= (isTwinXL ? 2 : 1)) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }
}, true);

// ─── Re-lock when variant changes ─────────────────────────────────────────────
// Horizon updates the URL when a size pill is clicked

window.addEventListener('popstate', () => setTimeout(applyPDPQtyLock, 100));

// Watch for URL change via History API (Horizon uses pushState)
const _pushState = history.pushState.bind(history);
history.pushState = function (...args) {
  _pushState(...args);
  setTimeout(applyPDPQtyLock, 150);
};

// Also watch for clicks on fieldset buttons (size pills)
document.addEventListener('click', function (e) {
  if (e.target.closest('product-form-component fieldset button, form.shopify-product-form fieldset button')) {
    setTimeout(applyPDPQtyLock, 150);
  }
});

// Watch quantity input changes
document.addEventListener('change', function (e) {
  if (e.target.closest('quantity-selector-component input')) {
    setTimeout(applyPDPQtyLock, 80);
  }
});

// ─── Init ─────────────────────────────────────────────────────────────────────

(function () {
  async function boot() {
    await loadProductVariants();
    applyPDPQtyLock();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // MutationObserver for Horizon re-renders
  let t = null;
  new MutationObserver(() => {
    clearTimeout(t);
    t = setTimeout(applyPDPQtyLock, 80);
  }).observe(document.body, { childList: true, subtree: true });
}());
}